import { Box, Tab, Tabs, Typography } from "@outschool/backpack";
import {
  LearnerBannerQuery,
  LearnerBannerQueryVariables,
} from "@outschool/gql-frontend-generated";
// This file is not yet translated.
/* eslint-disable i18next/no-literal-string */
import { useLocalStorageState } from "@outschool/local-storage";
import { dayjs } from "@outschool/time";
import { gql, useQueryWithPreviousData } from "@outschool/ui-apollo";
import { useFeatureFlag } from "@outschool/ui-components-shared";
import { ReadOnlyBanner } from "@outschool/ui-components-website";
import { Container, Loading } from "@outschool/ui-legacy-component-library";
import { UserAgent, useIsMobile, useIsSmall } from "@outschool/ui-utils";
import React from "react";

import defaultBanner from "../../../../images/banners/welcome/banner_bg.png";
import Link from "../../components/Link";
import * as Env from "../../Env";
import { learnerRoutes } from "../../lib/Routes";
import { useCurrentLearner } from "../../providers/CurrentLearnerProvider";
import Footer from "../nav/Footer";
import Header from "../nav/Header";
import CommunityBanner from "./CommunityBanner";
import {
  DESKTOP_BANNER_HEIGHT,
  MOBILE_BANNER_HEIGHT_NO_CONTENT,
} from "./consts";
import IosAppPromoBanner from "./IosAppPromoBanner";

type HeroLayoutWithTabsProps = {
  currentPath: string;
  children: React.ReactNode;
};

const LOCAL_STORAGE_KEY = "iOS.PromoBanner.FirstShownAt";
const DAYS_TO_SHOW_IOS_BANNER = 7;

function isWeekdayBusinessHours(): boolean {
  const now = dayjs().tz("America/New_York"); // EST/EDT timezone
  const dayOfWeek = now.day();
  const hour = now.hour();

  const isWeekday = dayOfWeek >= 1 && dayOfWeek <= 5;
  const isBusinessHours = hour >= 9 && hour < 17;

  return isWeekday && isBusinessHours;
}

export const learnerBannerQuery = gql`
  query LearnerBanner {
    contentfulLearnerBannerCollection(limit: 1) {
      items {
        name
        campaign {
          name
          startAt
          endAt
        }
        title
        subtitle
        ctaText
        ctaUrl
        image {
          title
          url
        }
      }
    }
  }
`;

function useCommunityBanner() {
  const { data, loading } = useQueryWithPreviousData<
    LearnerBannerQuery,
    LearnerBannerQueryVariables
  >(learnerBannerQuery);

  const communityBanner = data?.contentfulLearnerBannerCollection?.items?.[0];
  const isInDate =
    communityBanner?.campaign?.startAt &&
    dayjs().isAfter(communityBanner.campaign.startAt) &&
    communityBanner?.campaign?.endAt &&
    dayjs().isBefore(communityBanner.campaign.endAt);

  const featureFlag = useFeatureFlag("after-class-interest-explorer");

  if (
    featureFlag &&
    communityBanner?.campaign?.name === "interest_explorer" &&
    isWeekdayBusinessHours()
  ) {
    return {
      loading,
      communityBannerData: communityBanner,
    };
  }

  return {
    loading,
    communityBannerData: isInDate ? communityBanner : undefined,
  };
}

function useIosPromoBanner(): boolean | undefined {
  const [iosPromoBannerFirstShownAt, _setIosPromoBannerFirstShownAt] =
    useLocalStorageState(LOCAL_STORAGE_KEY, new Date().toISOString());
  if (
    (UserAgent.isIPhone() || UserAgent.isIPad()) &&
    dayjs().diff(iosPromoBannerFirstShownAt, "day") <= DAYS_TO_SHOW_IOS_BANNER
  ) {
    return true;
  } else {
    return false;
  }
}

function HeroLayoutWithTabs(
  { currentPath, children }: HeroLayoutWithTabsProps,
  ref: React.Ref<HTMLDivElement>,
) {
  const isMobile = useIsMobile();
  const showIosPromoBanner = useIosPromoBanner();
  const { loading, communityBannerData } = useCommunityBanner();
  const banner: "default" | "ios" | "community" = showIosPromoBanner
    ? "ios"
    : !!communityBannerData
    ? "community"
    : "default";

  if (loading) {
    return <Loading />;
  }

  return (
    <Box
      flex
      sx={{
        minHeight: "100vh",
        flexDirection: "column",
        backgroundColor: "common.white",
      }}
      ref={ref}
    >
      <Header
        sx={{
          background: isMobile || showIosPromoBanner ? "white" : undefined,
          borderColor: isMobile || showIosPromoBanner ? "#535353" : undefined,
          position: showIosPromoBanner ? "relative" : "absolute",
        }}
        includeBlob={!showIosPromoBanner}
      />
      {banner === "default" && <DefaultBanner />}
      {banner === "ios" && <IosAppPromoBanner />}
      {banner === "community" && communityBannerData && (
        <CommunityBanner bannerData={communityBannerData} />
      )}
      {Env.IS_READ_ONLY_MODE && <ReadOnlyBanner />}
      <NavigationTabs currentPath={currentPath} />
      <Container>{children}</Container>
      <Footer />
    </Box>
  );
}

const TABS = [
  {
    label: "Classes",
    path: learnerRoutes.dashboardPath(),
  },
  {
    label: "Search",
    path: learnerRoutes.findClassesPath(),
  },
  {
    label: "Favorites",
    path: learnerRoutes.savedClassesPath(),
  },
];

function NavigationTabs({ currentPath }: { currentPath: string }) {
  return (
    <Container sx={{ marginBottom: "1em" }}>
      <Tabs variant="scrollable" value={currentPath}>
        {TABS.map(tab => (
          <Tab
            key={tab.label}
            component={Link}
            label={tab.label}
            value={tab.path}
            to={tab.path}
          />
        ))}
      </Tabs>
    </Container>
  );
}

function DefaultBanner() {
  const isSmall = useIsSmall();
  const currentLearner = useCurrentLearner();
  return (
    <Box
      sx={(theme: $TSFixMe) => ({
        backgroundImage: `url("${defaultBanner}")`,
        backgroundSize: "cover",
        backgroundPosition: isSmall ? "left top" : "center top",
        height: MOBILE_BANNER_HEIGHT_NO_CONTENT,
        [theme.breakpoints.up("sm")]: {
          height: DESKTOP_BANNER_HEIGHT,
        },
      })}
      data-test-id="default-banner"
    >
      {!isSmall && (
        <Box
          flex
          sx={{
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            marginTop: "48px",
          }}
        >
          <Typography variant="h1" component="div">
            Welcome back,
          </Typography>
          <Typography variant="h1" component="div">
            {currentLearner?.name || "learner"}!
          </Typography>
        </Box>
      )}
    </Box>
  );
}

export default React.forwardRef(HeroLayoutWithTabs);
